import { useCallback, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { GalleryControls } from '@/libraries/design/components/gallery-controls/src/gallery-controls';
import { Image } from '@/libraries/design/components/image/src/image';
import { PullQuote } from '@/libraries/design/components/pull-quote/src/pull-quote';
import { wacToken } from '@/libraries/design/foundations';
import { useWacRefresh } from '@/utils/wac-refresh/wac-refresh-context';

export interface CustomerQuote {
	companyName: string;
	companyLogo: Logo[];
	quote: string;
	advocateName: string;
	advocateJobTitle: string;
}

export interface Logo {
	src: string;
	alt: string;
	href: string;
}

export interface CarouselProps {
	customerQuotes: CustomerQuote[];
}

export interface ChevronProps {
	type: 'left' | 'right';
}

const messages = {
	previousSlide: {
		id: 'customer-quote-carousel.previous-slide',
		defaultMessage: 'Previous slide',
	},
	nextSlide: {
		id: 'customer-quote-carousel.next-slide',
		defaultMessage: 'Next slide',
	},
};

export const CustomerQuoteCarousel = ({ customerQuotes }: CarouselProps) => {
	const isWacRefresh = useWacRefresh();

	const [activeQuoteIndex, setActiveQuoteIndex] = useState(0);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const prevSlide = useCallback(() => {
		if (activeQuoteIndex > 0) {
			setActiveQuoteIndex(activeQuoteIndex - 1);
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'previousSlide',
				source: 'customerQuoteCarousel',
				attributes: { label: 'previous slide' },
			}).fire();
		}
	}, [activeQuoteIndex, createAnalyticsEvent]);

	const nextSlide = useCallback(() => {
		if (activeQuoteIndex < customerQuotes.length - 1) {
			setActiveQuoteIndex(activeQuoteIndex + 1);
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'nextSlide',
				source: 'customerQuoteCarousel',
				attributes: { label: 'next slide' },
			}).fire();
		}
	}, [activeQuoteIndex, customerQuotes.length, createAnalyticsEvent]);

	const intl = useIntl();

	const logoSection = (
		<div css={gridCss}>
			{customerQuotes.map(({ companyLogo }, index) => (
				<div
					key={index}
					css={css({
						gridArea: '1/1',
						visibility: index === activeQuoteIndex ? 'visible' : 'hidden',
						width: '140px',
						height: '40px',
					})}
					aria-hidden={index !== activeQuoteIndex}
				>
					<Image
						src={companyLogo[0]?.src as string}
						alt={companyLogo[0]?.alt as string}
						style={{
							width: '100%',
							height: '100%',
							objectFit: 'contain',
							objectPosition: 'left',
						}}
					/>
				</div>
			))}
		</div>
	);

	const quoteSection = (
		<div css={[gridCss, overrideQuotePadding]}>
			<PullQuote
				quote={customerQuotes[activeQuoteIndex]?.quote as string}
				authorName={customerQuotes[activeQuoteIndex]?.advocateName as string}
				authorDetails={customerQuotes[activeQuoteIndex]?.advocateJobTitle as string}
			/>
		</div>
	);

	const legacyNavigationButtons = (
		<div css={buttonsContainerCss}>
			<button
				type="button"
				onClick={prevSlide}
				disabled={activeQuoteIndex === 0}
				css={buttonCss}
				aria-label={intl.formatMessage(messages.previousSlide)}
			>
				<div css={chevronCirlceCss}>
					<ChevronLeftIcon label={intl.formatMessage(messages.previousSlide)} />
				</div>
			</button>
			<p css={[lightTextCss, slideNumberCss]}>{`${activeQuoteIndex + 1}/${
				customerQuotes.length
			}`}</p>
			<button
				type="button"
				onClick={nextSlide}
				disabled={activeQuoteIndex === customerQuotes.length - 1}
				css={buttonCss}
				aria-label={intl.formatMessage(messages.nextSlide)}
			>
				<div css={chevronCirlceCss}>
					<ChevronRightIcon label={intl.formatMessage(messages.nextSlide)} />
				</div>
			</button>
		</div>
	);

	if (isWacRefresh) {
		return (
			<div css={gridCss}>
				<div aria-hidden={true} css={[overlapGridItemCss, hiddenCarouselContainerCss]}>
					<div css={[gridCss, hiddenQuoteCss]}>
						{customerQuotes.map((customerQuote, index) => (
							<div
								key={index}
								css={[overlapGridItemCss, visibilityHiddenCss, overrideQuotePadding]}
							>
								<PullQuote
									quote={customerQuote.quote}
									authorName={customerQuote.advocateName}
									authorDetails={customerQuote.advocateJobTitle}
								/>
							</div>
						))}
					</div>
				</div>
				<div
					css={[
						carouselContainerCss,
						refreshCarouselContainerCss,
						refreshTextCss,
						overlapGridItemCss,
					]}
				>
					<div css={refreshQuoteLayoutCss}>
						<div css={refreshLogoContainerCss}>{logoSection}</div>
						<div css={refreshQuoteContentCss}>{quoteSection}</div>
					</div>
					<div css={refreshControlsContainerCss}>
						<GalleryControls
							showCounter
							currentCount={activeQuoteIndex + 1}
							totalCount={customerQuotes.length}
							onPrevious={() => setActiveQuoteIndex(activeQuoteIndex - 1)}
							onNext={() => setActiveQuoteIndex(activeQuoteIndex + 1)}
							size="small"
						/>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div css={gridCss}>
			<div aria-hidden={true} css={[overlapGridItemCss, hiddenCarouselContainerCss]}>
				<div css={[gridCss, hiddenQuoteCss]}>
					{customerQuotes.map((customerQuote, index) => (
						<div key={index} css={[overlapGridItemCss, visibilityHiddenCss, overrideQuotePadding]}>
							<PullQuote
								quote={customerQuote.quote}
								authorName={customerQuote.advocateName}
								authorDetails={customerQuote.advocateJobTitle}
							/>
						</div>
					))}
				</div>
			</div>
			<div css={[carouselContainerCss, lightTextCss, overlapGridItemCss]}>
				<div css={logoAndButtonContainerCss}>
					{logoSection}
					{legacyNavigationButtons}
				</div>
				{quoteSection}
			</div>
		</div>
	);
};

const lightTextCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h2, p, span': {
		color: `${wacToken('color.neutral.dark1000')} !important`,
	},
});

const refreshTextCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'h2, p, span': {
		color: `${wacToken('color.primary.black')} !important`,
	},
});

const carouselContainerCss = css({
	borderRadius: wacToken('corner.radius.card'),
	backgroundColor: wacToken('color.neutral.dark100'),
	padding: token('space.400'),
	height: 'fit-content',
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300'),

	[media.below.sm]: {
		padding: token('space.300'),
	},
});

const refreshCarouselContainerCss = css({
	borderRadius: '20px',
	backgroundColor: wacToken('color.neutral.light0'),
	padding: token('space.600'),

	[media.below.sm]: {
		padding: token('space.300'),
	},
});

const refreshQuoteLayoutCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300'),

	[media.above.lg]: {
		flexDirection: 'row',
		gap: token('space.600'),
	},
});

const refreshLogoContainerCss = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	width: '140px',

	[media.above.lg]: {
		alignSelf: 'stretch',
	},
});

const refreshQuoteContentCss = css({
	flex: '1 1 0',
	minWidth: 0,
});

const refreshControlsContainerCss = css({
	display: 'flex',
	justifyContent: 'flex-end',
});

const logoAndButtonContainerCss = css({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});

const gridCss = css({
	display: 'grid',
	height: 'fit-content',
});

const overlapGridItemCss = css({
	gridArea: '1/1',
});

const buttonsContainerCss = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.150'),
});

const slideNumberCss = css({
	width: '21px',
	marginBlock: 0,
});

const buttonCss = css({
	height: '44px',
	width: '44px',
	backgroundColor: 'transparent',
	border: 'none',
	borderRadius: '100%',

	['&:hover']: {
		cursor: 'pointer',
	},

	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},

	['&:disabled']: {
		['&:hover']: {
			cursor: 'default',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		div: {
			cursor: 'default',
			backgroundColor: wacToken('color.neutral.light1000'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		path: {
			fill: wacToken('color.neutral.light800'),
		},
	},
});

const chevronCirlceCss = css({
	height: '32px',
	width: '32px',
	backgroundColor: wacToken('color.neutral.dark400'),
	border: 'none',
	borderRadius: '100%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',

	'&:hover': {
		cursor: 'pointer',
		backgroundColor: wacToken('color.neutral.dark500'),
	},
});

const hiddenCarouselContainerCss = css({
	padding: token('space.400'),

	[media.below.sm]: {
		padding: token('space.300'),
	},
});

const hiddenQuoteCss = css({
	paddingBlockStart: '68px',
});

const visibilityHiddenCss = css({
	visibility: 'hidden',
});

const overrideQuotePadding = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> div': {
		paddingBlock: 0,
	},
});
